import api from '@common/api';

import session from '@common/session';
import tracking from '@common/tracking';
import { AddBankAccountViaPlaidMutationVariables, BankDetail, BankDetailStatus } from '@services/appsync/types';
import { PlaidLinkOnSuccessMetadata } from 'react-plaid-link';
import { createStore, useStore } from 'zustand';
import { persist } from 'zustand/middleware';

const BLOCKED_BANK_ACCOUNT_MESSAGE = 'This bank or financial institution is blocked';

type APIResult = { success: boolean; error?: string };

interface BankAccountsModelProps {
  bankAccounts: BankDetail[];
}

interface BankAccountsModelActions {
  addBankAccount: (publicToken: string, metadata: PlaidLinkOnSuccessMetadata, existingBankAccountId?: string | null) => Promise<APIResult>;
  finishVerification: (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => Promise<APIResult>;
  deleteBankAccount: (bankAccountID: string) => Promise<APIResult>;
  fetchBankAccounts: () => Promise<APIResult & { bankAccounts?: BankDetail[] }>;
  getDefaultBankAccount: () => BankDetail | undefined;
  setDefaultBankAccount: (bankAccountID: string) => Promise<APIResult>;
  setBankAccounts: (bankAccounts: BankDetail[]) => void;
}
export const BankAccountsModel = createStore(
  persist<BankAccountsModelProps & BankAccountsModelActions>(
    (set, get) => ({
      addBankAccount: async (
        publicToken: string,
        metadata: PlaidLinkOnSuccessMetadata,
        existingBankAccountId?: string | null,
      ): Promise<{ success: boolean; error?: string }> => {
        const investorID = session.get()?.investorId;
        if (!investorID) return { error: 'No investor ID', success: false };
        try {
          const accountInfo = metadata.accounts[0];
          const payload: AddBankAccountViaPlaidMutationVariables = {
            ...{
              investor_account_id: investorID,
              public_token: publicToken,
            },
            ...(
              existingBankAccountId ? {
                bank_account_id: existingBankAccountId,
              }
              : {
                bank_account_id: accountInfo.id,
                bank_account_name: accountInfo.name,
                bank_account_type: accountInfo.type,
              }
            ),
          };
          await api.addBankAccount(payload);
          get().fetchBankAccounts();
          return { success: true };
        } catch (err: any) {
          if (err?.errors && err?.errors[0]?.message === BLOCKED_BANK_ACCOUNT_MESSAGE) {
            return {
              error:
                'This bank or financial institution is blocked for security reasons. Please link an account from a different institution',
              success: false,
            };
          }
          return { error: 'Unable to add bank account', success: false };
        }
      },
      bankAccounts: [],
      deleteBankAccount: async (bankAccountID: string) => {
        const investorID = session.get()?.investorId;
        if (!investorID) return { error: 'No investor ID', success: false };

        try {
          await api.deleteBankAccount({ bank_account_id: bankAccountID, investor_account_id: investorID });
          set({ bankAccounts: get().bankAccounts.filter((account) => account.id !== bankAccountID) });
          return { success: true };
        } catch {
          return { error: 'Unable to delete bank account', success: false };
        }
      },
      fetchBankAccounts: async () => {
        const investorID = session.get()?.investorId;
        if (!investorID) return { error: 'No investor ID', success: false };

        try {
          const accounts = await api.getBankAccounts({ investor_account_id: investorID });
          set({ bankAccounts: accounts });
          return {
            bankAccounts: accounts,
            success: true,
          };
        } catch {
          return { error: 'Unable to get bank accounts', success: false };
        }
      },
      finishVerification: async (publicToken, metaData) => {
        const investorID = session.get()?.investorId;
        if (!investorID) return { error: 'No investor ID', success: false };
        const account = get().bankAccounts?.find((account) => account.id === metaData.accounts[0].id);
        try {
          tracking.track({
            action: 'Bank Account Verification Started',
            is_default: Boolean(account?.default),
            name: account?.name ?? '',
            status: BankDetailStatus.PENDING_MANUAL_VERIFICATION,
            type: account?.account_type,
          });
          await api.finishPlaidBankAccountVerify({
            bank_account_id: metaData.accounts[0].id,
            investor_account_id: investorID,
          });
          tracking.track({
            action: 'Bank Account Verification Completed',
            is_default: Boolean(account?.default),
            name: account?.name ?? '',
            status: BankDetailStatus.VERIFIED,
            type: account?.account_type,
          });
          get().fetchBankAccounts();
          return { success: true };
        } catch (err: any) {
          tracking.track({
            action: 'Bank Account Verification Failed',
            is_default: Boolean(account?.default),
            name: account?.name ?? '',
            status: BankDetailStatus.PENDING_MANUAL_VERIFICATION,
            type: account?.account_type,
          });
          return { error: 'Unable to verify bank account', success: false };
        }
      },
      getDefaultBankAccount: () => {
        const { bankAccounts } = get();
        return bankAccounts.find((account) => account.default);
      },
      setBankAccounts: (bankAccounts) => {
        set({ bankAccounts });
      },
      setDefaultBankAccount: async (bankAccountID: string) => {
        const investorID = session.get()?.investorId;
        if (!investorID) return { error: 'No investor ID', success: false };
        try {
          const newDefault = await api.setDefaultBankAccount({
            bank_account_id: bankAccountID,
            investor_account_id: investorID,
          });
          if (newDefault) {
            tracking.track({
              action: 'Bank Account Default Set',
              is_default: true,
              name: newDefault.name,
              status: newDefault.status,
              type: newDefault.account_type,
            });
            set({
              bankAccounts: get().bankAccounts.map((account) => ({
                ...account,
                default: account.id === bankAccountID,
              })),
            });
            return { success: true };
          } else {
            return { error: 'Unable to set default bank account', success: false };
          }
        } catch {
          return { error: 'Unable to set default bank account', success: false };
        }
      },
    }),
    {
      getStorage: () => localStorage,
      name: 'bank-accounts-model',
    },
  ),
);

export function useBankAccountsModel(): BankAccountsModelProps & BankAccountsModelActions;
export function useBankAccountsModel<T>(
  selector: (state: BankAccountsModelProps & BankAccountsModelActions) => T,
  equals?: (a: T, b: T) => boolean,
): T;
export function useBankAccountsModel<T>(
  selector?: (state: BankAccountsModelProps & BankAccountsModelActions) => T,
  equals?: (a: T, b: T) => boolean,
) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return useStore(BankAccountsModel, selector!, equals);
}
